import { useEffect, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { WhatsAppHeader } from '@/components/whatsapp/WhatsAppWorkspace';
import { Button } from '@/components/Button';
import { Modal } from '@/components/Modal';
import { ModelPicker, ModelPickerValue } from '@/components/ModelPicker';
import { EnhancePromptButton } from '@/components/EnhancePromptButton';
import { botFlowsService, BotFlow, BotTriggerType, AiGeneratedFlowDraft } from '@/services/bot-flows.service';
import { botFlowGalleryService, DefaultBotFlow } from '@/services/bot-flow-gallery.service';
import { businessProfileService, BusinessProfile, INDUSTRY_OPTIONS } from '@/services/business-profile.service';
import { useActiveAccount } from '@/hooks/useActiveAccount';
import {
  Bot, Plus, Sparkles, Library,
  AlertCircle, RefreshCw, Save, Search, Tag, PenTool, ListChecks,
  Loader2, MoreVertical, Trash2, Pencil, Workflow,
} from 'lucide-react';
import toast from 'react-hot-toast';
import clsx from 'clsx';
import { formatDistanceToNow } from 'date-fns';

// ─── Helpers ─────────────────────────────────────────────────────────────────

const TRIGGER_LABELS: Record<BotTriggerType, string> = {
  ad: 'Ad', lead_ad_form: 'Lead Form', keyword: 'Keyword', manual: 'Manual', scheduled: 'Scheduled',
};

const TRIGGER_COLORS: Record<BotTriggerType, string> = {
  ad: 'bg-primary-soft text-primary',
  lead_ad_form: 'bg-info-soft text-info',
  keyword: 'bg-warning-soft text-warning',
  manual: 'bg-surface-alt text-text-secondary',
  scheduled: 'bg-success-soft text-success',
};

function triggerSummary(flow: BotFlow): string {
  const tc = flow.triggerConfig ?? {};
  const parts: string[] = [];
  if (tc.adIds?.length) parts.push(`${tc.adIds.length} ad${tc.adIds.length > 1 ? 's' : ''}`);
  if (tc.adSetIds?.length) parts.push(`${tc.adSetIds.length} ad set${tc.adSetIds.length > 1 ? 's' : ''}`);
  if (tc.campaignIds?.length) parts.push(`${tc.campaignIds.length} campaign${tc.campaignIds.length > 1 ? 's' : ''}`);
  if (tc.formIds?.length) parts.push(`${tc.formIds.length} form${tc.formIds.length > 1 ? 's' : ''}`);
  if (tc.keywords?.length) parts.push(`${tc.keywords.length} keyword${tc.keywords.length > 1 ? 's' : ''}`);
  return parts.length ? parts.join(', ') : 'No trigger config';
}

const inputCls = 'w-full rounded-xl border border-border bg-surface px-3 py-2 text-sm text-text-primary placeholder:text-text-muted focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary transition-colors';

// ─── AI Generate Modal ────────────────────────────────────────────────────────

interface AiGenerateModalProps {
  open: boolean;
  onClose: () => void;
  profile: BusinessProfile | null;
  accountUid?: string;
  onSaved: (flow: BotFlow) => void;
}

function AiGenerateModal({ open, onClose, profile, accountUid, onSaved }: AiGenerateModalProps) {
  const navigate = useNavigate();
  const [brief, setBrief] = useState(profile?.oneLiner ?? '');
  const [tone, setTone] = useState(profile?.tone ?? 'friendly');
  const [language, setLanguage] = useState(profile?.primaryLanguage ?? 'en');
  // The auto-generate flow is a GENERATION feature → user picks the model
  // explicitly. Defaults to the company default via ModelPicker. Enhancement
  // (the wand-2 button on the brief textarea) always uses the company default.
  const [aiPick, setAiPick] = useState<ModelPickerValue | undefined>(undefined);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [result, setResult] = useState<{ flow: AiGeneratedFlowDraft; reasoning: string } | null>(null);
  const [errors, setErrors] = useState<string[]>([]);

  // Sync brief with profile oneLiner when it changes
  useEffect(() => {
    if (profile?.oneLiner) setBrief(profile.oneLiner);
    if (profile?.tone) setTone(profile.tone);
    if (profile?.primaryLanguage) setLanguage(profile.primaryLanguage);
  }, [profile]);

  const handleGenerate = async () => {
    setLoading(true);
    setErrors([]);
    setResult(null);
    try {
      const res = await botFlowsService.aiGenerate({
        brief, tone, language,
        ai_provider: aiPick?.provider,
        ai_model: aiPick?.model,
      });
      setResult(res);
    } catch (err: unknown) {
      const e = err as { response?: { data?: { errors?: string[]; message?: string } } };
      const errs = e?.response?.data?.errors ?? [e?.response?.data?.message ?? 'Generation failed'];
      setErrors(errs);
      toast.error('AI generation failed. See errors below.');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!result) return;
    setSaving(true);
    try {
      // Bind the new flow to the active WhatsApp account so it only triggers
      // for inbound messages on that account (multi-account isolation).
      const payload = { ...(result.flow as any), accountUid };
      const saved = await botFlowsService.saveDraft(payload);
      toast.success('Draft saved!');
      onSaved(saved);
      navigate(`/leads/bot-flows/${saved.flowUid}`);
      onClose();
    } catch {
      toast.error('Failed to save draft');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal open={open} onClose={onClose} title="Auto-generate bot flow" widthClass="max-w-2xl">
      {!profile && (
        <div className="mb-4 flex items-start gap-2 rounded-xl border border-amber-200 bg-amber-50 dark:border-amber-700/30 dark:bg-amber-900/20 p-3">
          <AlertCircle size={15} className="mt-0.5 shrink-0 text-amber-600 dark:text-amber-400" />
          <div className="text-sm text-amber-700 dark:text-amber-300">
            Tell us about your business first for a better result.{' '}
            <a href="/settings/business-profile" className="font-semibold underline">Set up Business Profile →</a>
          </div>
        </div>
      )}

      {!result ? (
        <div className="space-y-4">
          <div>
            <div className="mb-1.5 flex items-center justify-between gap-2">
              <label className="block text-[13px] font-medium text-text-secondary">Brief</label>
              <EnhancePromptButton
                value={brief}
                onChange={setBrief}
                context="WhatsApp bot flow brief"
                disabled={loading}
              />
            </div>
            <textarea
              rows={3}
              className={clsx(inputCls, 'resize-none')}
              value={brief}
              onChange={e => setBrief(e.target.value)}
              placeholder="e.g. Qualify incoming leads from our Black Friday ad — capture name, interest, and budget"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="mb-1.5 block text-[13px] font-medium text-text-secondary">Tone</label>
              <div className="flex gap-1.5">
                {(['formal', 'friendly', 'casual'] as const).map(t => (
                  <button key={t} onClick={() => setTone(t)}
                    className={clsx(
                      'flex-1 rounded-lg border py-1.5 text-xs font-medium capitalize transition-colors',
                      tone === t ? 'border-primary bg-primary-soft text-primary' : 'border-border text-text-secondary hover:border-primary/50',
                    )}>
                    {t}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label className="mb-1.5 block text-[13px] font-medium text-text-secondary">Language</label>
              <input type="text" className={inputCls} value={language} onChange={e => setLanguage(e.target.value)} placeholder="en" />
            </div>
          </div>

          <div>
            <label className="mb-1.5 block text-[13px] font-medium text-text-secondary">AI model</label>
            <ModelPicker value={aiPick} onChange={setAiPick} className="w-full" />
            <div className="mt-1 text-[11px] text-text-muted">
              Defaults to your company default. Change it under <a className="underline" href="/settings/ai">Settings → AI</a>.
            </div>
          </div>

          {errors.length > 0 && (
            <div className="rounded-xl border border-danger/30 bg-danger-soft p-3 space-y-1">
              {errors.map((e, i) => (
                <div key={i} className="flex items-start gap-1.5 text-xs text-danger">
                  <AlertCircle size={12} className="mt-0.5 shrink-0" /> {e}
                </div>
              ))}
            </div>
          )}

          <div className="flex justify-end pt-2">
            <Button loading={loading} iconLeft={<Sparkles size={14} />} onClick={handleGenerate}>
              Generate
            </Button>
          </div>
        </div>
      ) : (
        <div className="space-y-4">
          {/* Reasoning */}
          <div className="rounded-xl border border-border bg-surface-alt p-3">
            <div className="text-xs font-semibold text-text-muted mb-1">AI reasoning</div>
            <div className="text-sm text-text-secondary">{result.reasoning}</div>
          </div>

          {/* States preview */}
          <div>
            <div className="text-xs font-semibold text-text-muted mb-2">{result.flow.states.length} states</div>
            <div className="space-y-1 max-h-56 overflow-y-auto pr-1">
              {result.flow.states.map((s, i) => (
                <div key={i} className="flex items-center gap-2 rounded-lg border border-border bg-surface px-3 py-2 text-sm">
                  <span className="shrink-0 rounded-md bg-primary-soft px-1.5 py-0.5 text-[11px] font-mono font-medium text-primary">{s.type}</span>
                  <span className="font-medium text-text-primary">{s.id}</span>
                  {(s as any).prompt && (
                    <span className="truncate text-text-muted text-xs">{(s as any).prompt}</span>
                  )}
                </div>
              ))}
            </div>
          </div>

          <div className="flex gap-2 pt-2">
            <Button variant="secondary" iconLeft={<RefreshCw size={14} />} onClick={() => setResult(null)}>
              Try again
            </Button>
            <Button loading={saving} iconLeft={<Save size={14} />} onClick={handleSave}>
              Save draft
            </Button>
          </div>
        </div>
      )}
    </Modal>
  );
}

// ─── Gallery Modal ────────────────────────────────────────────────────────────

interface GalleryModalProps {
  open: boolean;
  onClose: () => void;
  defaultIndustry?: string;
  accountUid?: string;
  onCloned: (flow: { flowUid: string; name: string }) => void;
}

function BotFlowGalleryModal({ open, onClose, defaultIndustry, accountUid, onCloned }: GalleryModalProps) {
  const navigate = useNavigate();
  const [flows, setFlows] = useState<DefaultBotFlow[]>([]);
  const [loading, setLoading] = useState(false);
  const [cloning, setCloning] = useState<string | null>(null);
  const [industryFilter, setIndustryFilter] = useState(defaultIndustry ?? '');
  const [search, setSearch] = useState('');

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    botFlowGalleryService.list({ industry: industryFilter || undefined, q: search || undefined, limit: 50 })
      .then(r => setFlows(r.items))
      .catch(() => toast.error('Failed to load gallery'))
      .finally(() => setLoading(false));
  }, [open, industryFilter, search]);

  const handleClone = async (uid: string) => {
    setCloning(uid);
    try {
      const cloned = await botFlowGalleryService.clone(uid, accountUid);
      toast.success('Flow added to your workspace');
      onCloned(cloned);
      navigate(`/leads/bot-flows/${cloned.flowUid}`);
      onClose();
    } catch {
      toast.error('Failed to clone flow');
    } finally {
      setCloning(null);
    }
  };

  return (
    <Modal open={open} onClose={onClose} title="Flow gallery" widthClass="max-w-2xl">
      {/* Filters */}
      <div className="mb-4 flex gap-2">
        <div className="relative flex-1">
          <Search size={14} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-text-muted" />
          <input
            type="text" placeholder="Search flows..." value={search}
            onChange={e => setSearch(e.target.value)}
            className={clsx(inputCls, 'pl-9')}
          />
        </div>
        <select
          value={industryFilter}
          onChange={e => setIndustryFilter(e.target.value)}
          className={clsx(inputCls, 'w-44')}
        >
          <option value="">All industries</option>
          {INDUSTRY_OPTIONS.map(o => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
      </div>

      {/* Grid */}
      {loading ? (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          {[...Array(4)].map((_, i) => <div key={i} className="skeleton h-28 rounded-2xl" />)}
        </div>
      ) : flows.length === 0 ? (
        <div className="py-12 text-center text-sm text-text-muted">No flows found.</div>
      ) : (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 max-h-[400px] overflow-y-auto pr-1">
          {flows.map(flow => (
            <div
              key={flow.defaultFlowUid}
              className="flex flex-col rounded-2xl border border-border bg-surface p-4 hover:border-primary/40 hover:shadow-soft transition-all"
            >
              <div className="flex items-start justify-between gap-2 mb-2">
                <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-primary-soft text-primary">
                  <Bot size={15} />
                </div>
                <span className="rounded-full bg-surface-alt px-2 py-0.5 text-[10px] font-mono text-text-muted">
                  {flow.states.length} states
                </span>
              </div>
              <div className="font-semibold text-sm text-text-primary mb-1">{flow.title}</div>
              {flow.description && (
                <div className="text-xs text-text-muted mb-2 line-clamp-2">{flow.description}</div>
              )}
              {flow.tags.length > 0 && (
                <div className="flex flex-wrap gap-1 mb-3">
                  {flow.tags.slice(0, 3).map(t => (
                    <span key={t} className="inline-flex items-center gap-0.5 rounded-full bg-surface-alt px-2 py-0.5 text-[10px] text-text-muted">
                      <Tag size={8} /> {t}
                    </span>
                  ))}
                </div>
              )}
              <div className="mt-auto">
                <Button
                  size="sm"
                  variant="secondary"
                  className="w-full"
                  loading={cloning === flow.defaultFlowUid}
                  onClick={() => handleClone(flow.defaultFlowUid)}
                >
                  Use this
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </Modal>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────

export default function BotFlowsList() {
  const navigate = useNavigate();
  const location = useLocation();
  const [flows, setFlows] = useState<BotFlow[]>([]);
  const [loading, setLoading] = useState(true);
  const [toggling, setToggling] = useState<string | null>(null);
  const [profile, setProfile] = useState<BusinessProfile | null>(null);
  const [showAiModal, setShowAiModal] = useState(false);
  const [showGalleryModal, setShowGalleryModal] = useState(false);
  const [creatingBlank, setCreatingBlank] = useState(false);
  const [listSearch, setListSearch] = useState('');
  const [listStatus, setListStatus] = useState<'all' | 'active' | 'inactive'>('all');
  const [menuOpen, setMenuOpen] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<BotFlow | null>(null);
  const [deleting, setDeleting] = useState(false);
  const menuRef = useRef<HTMLDivElement | null>(null);

  // Close kebab menu on outside click / Escape
  useEffect(() => {
    if (!menuOpen) return;
    const onDocClick = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) setMenuOpen(null);
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setMenuOpen(null); };
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [menuOpen]);

  // Scope the list to the active WhatsApp account. Legacy flows (no
  // accountUid) still appear so users can see and reassign them.
  const { active: activeAccount, loading: accountsLoading } = useActiveAccount();

  const load = async (filter?: { q?: string; status?: 'active' | 'inactive' }) => {
    setLoading(true);
    try {
      const [data, p] = await Promise.all([
        botFlowsService.list({ ...filter, accountUid: activeAccount?.accountUid }),
        profile ? Promise.resolve(profile) : businessProfileService.getByCompany().catch(() => null),
      ]);
      setFlows(data);
      if (!profile && p) setProfile(p);
    } catch {
      toast.error('Failed to load bot flows');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Don't query before the account picker resolves — an unscoped call
    // returns rows from every connected WhatsApp number.
    if (accountsLoading) return;
    load();
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [activeAccount?.accountUid, accountsLoading]);

  // Debounced server-side search/filter
  useEffect(() => {
    const t = setTimeout(() => {
      load({
        q: listSearch || undefined,
        status: listStatus === 'all' ? undefined : listStatus,
      });
    }, 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listSearch, listStatus]);

  // Auto-trigger generate modal when navigated from Business Profile page
  useEffect(() => {
    if ((location.state as Record<string, unknown>)?.autoGenerate) {
      setShowAiModal(true);
      // Clear state so refresh doesn't re-trigger
      window.history.replaceState({}, '');
    }
  }, [location.state]);

  const handleNewFlow = async () => {
    try {
      const created = await botFlowsService.create({
        name: 'Untitled flow', triggerType: 'ad', triggerConfig: {},
        accountUid: activeAccount?.accountUid,
        states: [{ id: 'end', type: 'end', terminalStatus: 'qualified' }],
        startStateId: 'end', isActive: false,
      });
      navigate(`/leads/bot-flows/${created.flowUid}`);
    } catch {
      toast.error('Failed to create flow');
    }
  };

  const handleDesignFromScratch = async () => {
    setCreatingBlank(true);
    try {
      const created = await botFlowsService.create({
        name: 'Untitled flow',
        accountUid: activeAccount?.accountUid,
        triggerType: 'manual',
        triggerConfig: {},
        states: [
          {
            id: 'start',
            type: 'message',
            prompt: 'Hi {{profileName}}, thanks for reaching out!',
            next: undefined,
          },
        ],
        startStateId: 'start',
        isActive: false,
      });
      navigate(`/leads/bot-flows/${created.flowUid}`);
    } catch {
      toast.error('Failed to create flow');
    } finally {
      setCreatingBlank(false);
    }
  };

  const handleDelete = async () => {
    if (!confirmDelete) return;
    setDeleting(true);
    try {
      await botFlowsService.remove(confirmDelete.flowUid);
      setFlows(prev => prev.filter(f => f.flowUid !== confirmDelete.flowUid));
      toast.success('Flow deleted');
      setConfirmDelete(null);
    } catch {
      toast.error('Failed to delete flow');
    } finally {
      setDeleting(false);
    }
  };

  const handleToggle = async (flow: BotFlow, e: React.MouseEvent) => {
    e.stopPropagation();
    setToggling(flow.flowUid);
    try {
      const updated = flow.isActive
        ? await botFlowsService.deactivate(flow.flowUid)
        : await botFlowsService.activate(flow.flowUid);
      setFlows(prev => prev.map(f => f.flowUid === flow.flowUid ? updated : f));
      toast.success(updated.isActive ? 'Flow activated' : 'Flow deactivated');
    } catch {
      toast.error('Failed to toggle flow');
    } finally {
      setToggling(null);
    }
  };

  const profileIndustry = profile?.industry;

  return (
    <>
      <WhatsAppHeader section="automation"
        title="Automation"
        breadcrumb="WhatsApp / Automation"
        description="Design conversations, qualify customers and bring in your team at the right moment."
        actions={
          <Button size="sm" iconLeft={<Plus size={14} />} onClick={handleNewFlow}>
            New flow
          </Button>
        }
      />

      <div className="px-7 py-7 w-full">
        {/* CTA Cards */}
        <div className="mb-6 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {/* AI Generate */}
          <div
            className="group relative flex cursor-pointer flex-col gap-3 overflow-hidden rounded-2xl border border-primary/30 p-5 transition-all motion-safe:hover:-translate-y-0.5 hover:shadow-elevated"
            style={{ background: 'linear-gradient(135deg, var(--color-primary-soft) 0%, var(--color-surface) 100%)' }}
            onClick={() => setShowAiModal(true)}
          >
            <div className="pointer-events-none absolute -right-8 -top-8 h-32 w-32 rounded-full bg-primary/10 blur-2xl" />
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary-soft text-primary shadow-soft">
                <Sparkles size={20} />
              </div>
              <div>
                <div className="text-sm font-bold text-text-primary">Auto-generate</div>
                <div className="text-xs text-text-muted">AI builds a tailored flow in seconds</div>
              </div>
            </div>
            {!profile && (
              <div className="flex items-center gap-1.5 text-xs text-amber-600 dark:text-amber-400">
                <AlertCircle size={12} /> Tell us about your business first →
              </div>
            )}
          </div>

          {/* Gallery */}
          <div
            className="group flex cursor-pointer flex-col gap-3 overflow-hidden rounded-2xl border border-border p-5 transition-all motion-safe:hover:-translate-y-0.5 hover:border-primary/30 hover:shadow-elevated bg-surface"
            onClick={() => setShowGalleryModal(true)}
          >
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-surface-alt text-text-secondary">
                <Library size={20} />
              </div>
              <div>
                <div className="text-sm font-bold text-text-primary">Browse gallery</div>
                <div className="text-xs text-text-muted">10 industry presets ready to use</div>
              </div>
            </div>
          </div>

          {/* Design from scratch */}
          <div
            className="group relative flex cursor-pointer flex-col gap-3 overflow-hidden rounded-2xl border border-info/30 p-5 transition-all motion-safe:hover:-translate-y-0.5 hover:shadow-elevated"
            style={{ background: 'linear-gradient(135deg, var(--color-info-soft) 0%, var(--color-surface) 100%)' }}
            onClick={creatingBlank ? undefined : handleDesignFromScratch}
            aria-disabled={creatingBlank}
          >
            <div className="pointer-events-none absolute -right-8 -top-8 h-32 w-32 rounded-full bg-info/10 blur-2xl" />
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-info-soft text-info shadow-soft">
                {creatingBlank ? <Loader2 size={20} className="animate-spin" /> : <PenTool size={20} />}
              </div>
              <div>
                <div className="text-sm font-bold text-text-primary">Design from scratch</div>
                <div className="text-xs text-text-muted">Open the visual canvas editor</div>
              </div>
            </div>
          </div>

          {/* Step-by-step builder */}
          <div
            className="group flex cursor-pointer flex-col gap-3 overflow-hidden rounded-2xl border border-success/30 p-5 transition-all motion-safe:hover:-translate-y-0.5 hover:shadow-elevated"
            style={{ background: 'linear-gradient(135deg, var(--color-success-soft) 0%, var(--color-surface) 100%)' }}
            onClick={() => navigate('/leads/bot-flows/wizard')}
          >
            <div className="pointer-events-none absolute -right-8 -top-8 h-32 w-32 rounded-full bg-success/10 blur-2xl" />
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-success-soft text-success shadow-soft">
                <ListChecks size={20} />
              </div>
              <div>
                <div className="text-sm font-bold text-text-primary">Step-by-step builder</div>
                <div className="text-xs text-text-muted">Answer questions, we build the flow</div>
              </div>
            </div>
          </div>
        </div>

        {/* Search & status filter — shown when there are flows or a filter is active */}
        {(flows.length > 0 || listSearch || listStatus !== 'all') && (
          <div className="mb-4 flex flex-col gap-2 sm:flex-row sm:items-center">
            <div className="relative flex-1">
              <Search size={14} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-text-muted" />
              <input
                type="text"
                value={listSearch}
                onChange={(e) => setListSearch(e.target.value)}
                placeholder="Search flows by name…"
                className="w-full rounded-xl border border-border bg-surface py-2 pl-9 pr-3 text-sm text-text-primary placeholder:text-text-muted focus:outline-none focus:border-primary focus:ring-4 focus:ring-primary-soft"
              />
            </div>
            <div className="inline-flex rounded-xl border border-border bg-surface p-0.5">
              {(['all', 'active', 'inactive'] as const).map((opt) => (
                <button
                  key={opt}
                  onClick={() => setListStatus(opt)}
                  className={`rounded-lg px-3 py-1.5 text-xs font-medium capitalize transition-colors ${
                    listStatus === opt ? 'bg-primary-soft text-primary' : 'text-text-muted hover:text-text-primary'
                  }`}
                >
                  {opt}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Flow list */}
        {loading ? (
          <div className="space-y-3">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="skeleton h-16 rounded-2xl" />
            ))}
          </div>
        ) : flows.length === 0 && (listSearch || listStatus !== 'all') ? (
          <div className="rounded-2xl border border-border bg-surface p-8 text-center">
            <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-2xl bg-surface-alt text-text-muted">
              <Search size={22} />
            </div>
            <h3 className="font-display text-base font-semibold text-text-primary">No flows match your filter</h3>
            <p className="mt-1 text-sm text-text-muted">Try a different search term or clear the status filter.</p>
            <button
              onClick={() => { setListSearch(''); setListStatus('all'); }}
              className="mt-4 inline-flex items-center gap-1.5 rounded-lg border border-border bg-surface-alt px-3 py-1.5 text-xs font-semibold text-text-secondary transition hover:border-primary/30"
            >
              Clear filters
            </button>
          </div>
        ) : flows.length === 0 ? (
          <div className="rounded-2xl border border-border bg-surface p-8 text-center">
            <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-primary-soft text-primary">
              <Bot size={28} />
            </div>
            <h3 className="font-display text-lg font-semibold text-text-primary">Pick the path that fits you best</h3>
            <p className="mt-1.5 text-sm text-text-muted">
              Choose one of the four options above to create your first automated conversation flow.
            </p>
            <div className="mt-6 flex flex-wrap justify-center gap-2">
              <button
                onClick={() => setShowAiModal(true)}
                className="inline-flex items-center gap-1.5 rounded-lg border border-primary/30 bg-primary-soft px-3 py-1.5 text-xs font-semibold text-primary transition hover:brightness-110"
              >
                <Sparkles size={12} /> Auto-generate
              </button>
              <button
                onClick={() => setShowGalleryModal(true)}
                className="inline-flex items-center gap-1.5 rounded-lg border border-border bg-surface-alt px-3 py-1.5 text-xs font-semibold text-text-secondary transition hover:border-primary/30"
              >
                <Library size={12} /> Browse gallery
              </button>
              <button
                onClick={creatingBlank ? undefined : handleDesignFromScratch}
                disabled={creatingBlank}
                className="inline-flex items-center gap-1.5 rounded-lg border border-info/30 bg-info-soft px-3 py-1.5 text-xs font-semibold text-info transition hover:brightness-110 disabled:opacity-50"
              >
                {creatingBlank ? <Loader2 size={12} className="animate-spin" /> : <PenTool size={12} />}
                Design from scratch
              </button>
              <button
                onClick={() => navigate('/leads/bot-flows/wizard')}
                className="inline-flex items-center gap-1.5 rounded-lg border border-success/30 bg-success-soft px-3 py-1.5 text-xs font-semibold text-success transition hover:brightness-110"
              >
                <ListChecks size={12} /> Step-by-step
              </button>
            </div>
          </div>
        ) : (
          <>
            <div className="mb-3 flex items-center justify-between px-1">
              <div className="text-xs font-medium text-text-muted">
                <span className="text-text-primary">{flows.length}</span> flow{flows.length !== 1 ? 's' : ''}
                {' · '}
                <span className="text-green-600 dark:text-green-400">{flows.filter(f => f.isActive).length}</span> active
              </div>
            </div>
            <div className="overflow-hidden rounded-2xl border border-border bg-surface divide-y divide-border">
              {flows.map((flow) => {
                const isToggling = toggling === flow.flowUid;
                return (
                  <div
                    key={flow.flowUid}
                    className="group relative flex items-center gap-4 px-4 py-3.5 cursor-pointer transition-colors hover:bg-surface-alt"
                    onClick={() => navigate(`/leads/bot-flows/${flow.flowUid}`)}
                  >
                    <div className={clsx(
                      'relative flex h-10 w-10 shrink-0 items-center justify-center rounded-xl transition-colors',
                      flow.isActive
                        ? 'bg-green-50 text-green-600 dark:bg-green-900/20 dark:text-green-400'
                        : 'bg-primary-soft text-primary',
                    )}>
                      <Bot size={18} />
                      {flow.isActive && (
                        <span className="absolute -right-0.5 -top-0.5 h-2.5 w-2.5 rounded-full bg-green-500 ring-2 ring-surface" />
                      )}
                    </div>

                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-semibold text-sm text-text-primary truncate">{flow.name}</span>
                        <span className={clsx(
                          'rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide shrink-0',
                          TRIGGER_COLORS[flow.triggerType],
                        )}>
                          {TRIGGER_LABELS[flow.triggerType]}
                        </span>
                        {!flow.accountUid && (
                          <span className="rounded-full border border-dashed border-text-muted/40 px-1.5 py-0.5 text-[9px] font-medium text-text-muted" title="Legacy flow — any WhatsApp account can fire it">
                            Any account
                          </span>
                        )}
                      </div>
                      <div className="mt-1 flex items-center gap-x-3 gap-y-0.5 flex-wrap text-xs text-text-muted">
                        <span className="inline-flex items-center gap-1">
                          <Workflow size={11} />
                          {flow.states.length} state{flow.states.length !== 1 ? 's' : ''}
                        </span>
                        <span className="text-border">•</span>
                        <span className="truncate">{triggerSummary(flow)}</span>
                        {flow.updatedAt && (
                          <>
                            <span className="text-border">•</span>
                            <span>Updated {formatDistanceToNow(new Date(flow.updatedAt), { addSuffix: true })}</span>
                          </>
                        )}
                      </div>
                    </div>

                    {/* Toggle switch */}
                    <button
                      type="button"
                      role="switch"
                      aria-checked={flow.isActive}
                      aria-label={flow.isActive ? 'Deactivate flow' : 'Activate flow'}
                      disabled={isToggling}
                      onClick={(e) => handleToggle(flow, e)}
                      className={clsx(
                        'relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-primary/40 focus:ring-offset-2 focus:ring-offset-surface',
                        flow.isActive ? 'bg-green-500' : 'bg-border',
                        isToggling && 'opacity-60 cursor-wait',
                      )}
                    >
                      <span
                        className={clsx(
                          'inline-block h-5 w-5 transform rounded-full bg-white shadow-soft transition-transform',
                          flow.isActive ? 'translate-x-5' : 'translate-x-0.5',
                        )}
                      />
                    </button>

                    {/* Kebab menu */}
                    <div className="relative shrink-0" ref={menuOpen === flow.flowUid ? menuRef : undefined}>
                      <button
                        type="button"
                        aria-label="Flow actions"
                        onClick={(e) => {
                          e.stopPropagation();
                          setMenuOpen(menuOpen === flow.flowUid ? null : flow.flowUid);
                        }}
                        className="flex h-8 w-8 items-center justify-center rounded-lg text-text-muted hover:bg-border hover:text-text-primary transition-colors"
                      >
                        <MoreVertical size={16} />
                      </button>
                      {menuOpen === flow.flowUid && (
                        <div
                          className="absolute right-0 top-full z-20 mt-1 w-40 overflow-hidden rounded-xl border border-border bg-surface shadow-elevated"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <button
                            type="button"
                            onClick={() => {
                              setMenuOpen(null);
                              navigate(`/leads/bot-flows/${flow.flowUid}`);
                            }}
                            className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-text-secondary hover:bg-surface-alt hover:text-text-primary"
                          >
                            <Pencil size={13} /> Edit
                          </button>
                          <button
                            type="button"
                            onClick={() => {
                              setMenuOpen(null);
                              setConfirmDelete(flow);
                            }}
                            className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-danger hover:bg-danger-soft"
                          >
                            <Trash2 size={13} /> Delete
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>

      {/* Modals */}
      <AiGenerateModal
        open={showAiModal}
        onClose={() => setShowAiModal(false)}
        profile={profile}
        accountUid={activeAccount?.accountUid}
        onSaved={(flow) => setFlows(prev => [flow, ...prev])}
      />
      <BotFlowGalleryModal
        open={showGalleryModal}
        onClose={() => setShowGalleryModal(false)}
        defaultIndustry={profileIndustry}
        accountUid={activeAccount?.accountUid}
        onCloned={(cloned) => {
          botFlowsService.get(cloned.flowUid).then(flow => setFlows(prev => [flow, ...prev])).catch(() => undefined);
        }}
      />

      <Modal
        open={!!confirmDelete}
        onClose={() => !deleting && setConfirmDelete(null)}
        title="Delete bot flow?"
        widthClass="max-w-md"
      >
        <div className="space-y-4">
          <div className="flex items-start gap-3 rounded-xl border border-danger/30 bg-danger-soft p-3">
            <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-danger/15 text-danger">
              <Trash2 size={16} />
            </div>
            <div className="text-sm text-text-secondary">
              <span className="font-semibold text-text-primary">{confirmDelete?.name}</span> will be permanently deleted.
              This cannot be undone, and any leads currently in this flow will stop receiving automated messages.
            </div>
          </div>
          <div className="flex justify-end gap-2 pt-1">
            <Button variant="secondary" onClick={() => setConfirmDelete(null)} disabled={deleting}>
              Cancel
            </Button>
            <Button variant="danger" loading={deleting} iconLeft={<Trash2 size={14} />} onClick={handleDelete}>
              Delete flow
            </Button>
          </div>
        </div>
      </Modal>
    </>
  );
}
